const infraTest = {
  id: 'infra',
  name: 'Infrastructure Observability',
  subtitle: 'Mock interview · Data Engineer loop',
  description: 'Service health monitoring and incident analysis',
  pinnedNow: '2026-04-15 14:00:00',

  schema: `
    CREATE TABLE services (
      service_id TEXT PRIMARY KEY,
      service_name TEXT,
      team TEXT,
      tier INTEGER,
      sla_target REAL
    );

    CREATE TABLE metrics (
      metric_id TEXT PRIMARY KEY,
      service_id TEXT,
      metric_type TEXT,
      recorded_at TEXT,
      value REAL,
      FOREIGN KEY (service_id) REFERENCES services(service_id)
    );

    CREATE TABLE alerts (
      alert_id TEXT PRIMARY KEY,
      service_id TEXT,
      severity TEXT,
      fired_at TEXT,
      resolved_at TEXT,
      alert_type TEXT,
      FOREIGN KEY (service_id) REFERENCES services(service_id)
    );

    CREATE TABLE incidents (
      incident_id TEXT PRIMARY KEY,
      service_id TEXT,
      started_at TEXT,
      resolved_at TEXT,
      impact TEXT,
      root_cause TEXT,
      FOREIGN KEY (service_id) REFERENCES services(service_id)
    );
  `,

  seedDataFn: (db) => {
    // Services - tier 1 is highest priority, sla_target is uptime percentage
    const services = [
      ['s1','AuthService','platform',1,99.95],
      ['s2','PaymentAPI','payments',1,99.99],
      ['s3','UserDB','platform',1,99.95],
      ['s4','NotificationService','engagement',2,99.5],
      ['s5','AnalyticsCollector','data',2,99.0],
      ['s6','RecommendationEngine','ml',2,99.5],
      ['s7','ImageProcessor','media',3,98.0],
      ['s8','EmailService','engagement',3,95.0],
      ['s9','SearchIndexer','search',2,99.5],
      ['s10','CacheLayer','platform',1,99.9]
    ];

    const serviceStmt = db.prepare(
      'INSERT INTO services (service_id, service_name, team, tier, sla_target) VALUES (?, ?, ?, ?, ?)'
    );
    services.forEach(s => serviceStmt.run(s));
    serviceStmt.free();

    // Metrics - Q1: Calculate p95 latency for last 24h
    const metrics = [
      // AuthService - good latency (20 measurements for NTILE(20))
      ['m1','s1','latency_ms','2026-04-15 13:00',45],
      ['m2','s1','latency_ms','2026-04-15 13:03',52],
      ['m3','s1','latency_ms','2026-04-15 13:06',48],
      ['m4','s1','latency_ms','2026-04-15 13:09',51],
      ['m5','s1','latency_ms','2026-04-15 13:12',47],
      ['m6','s1','latency_ms','2026-04-15 13:15',49],
      ['m7','s1','latency_ms','2026-04-15 13:18',53],
      ['m8','s1','latency_ms','2026-04-15 13:21',46],
      ['m9','s1','latency_ms','2026-04-15 13:24',50],
      ['m10','s1','latency_ms','2026-04-15 13:27',44],
      ['m201','s1','latency_ms','2026-04-15 13:30',48],
      ['m202','s1','latency_ms','2026-04-15 13:33',51],
      ['m203','s1','latency_ms','2026-04-15 13:36',49],
      ['m204','s1','latency_ms','2026-04-15 13:39',52],
      ['m205','s1','latency_ms','2026-04-15 13:42',47],
      ['m206','s1','latency_ms','2026-04-15 13:45',46],
      ['m207','s1','latency_ms','2026-04-15 13:48',50],
      ['m208','s1','latency_ms','2026-04-15 13:51',48],
      ['m209','s1','latency_ms','2026-04-15 13:54',54],
      ['m210','s1','latency_ms','2026-04-15 13:57',150], // p95 spike

      // PaymentAPI - variable latency with spikes (20 measurements)
      ['m11','s2','latency_ms','2026-04-15 13:00',80],
      ['m12','s2','latency_ms','2026-04-15 13:03',85],
      ['m13','s2','latency_ms','2026-04-15 13:06',82],
      ['m14','s2','latency_ms','2026-04-15 13:09',88],
      ['m15','s2','latency_ms','2026-04-15 13:12',91],
      ['m16','s2','latency_ms','2026-04-15 13:15',84],
      ['m17','s2','latency_ms','2026-04-15 13:18',87],
      ['m18','s2','latency_ms','2026-04-15 13:21',89],
      ['m19','s2','latency_ms','2026-04-15 13:24',83],
      ['m20','s2','latency_ms','2026-04-15 13:27',86],
      ['m211','s2','latency_ms','2026-04-15 13:30',90],
      ['m212','s2','latency_ms','2026-04-15 13:33',85],
      ['m213','s2','latency_ms','2026-04-15 13:36',82],
      ['m214','s2','latency_ms','2026-04-15 13:39',87],
      ['m215','s2','latency_ms','2026-04-15 13:42',84],
      ['m216','s2','latency_ms','2026-04-15 13:45',88],
      ['m217','s2','latency_ms','2026-04-15 13:48',91],
      ['m218','s2','latency_ms','2026-04-15 13:51',450], // spike
      ['m219','s2','latency_ms','2026-04-15 13:54',86],
      ['m220','s2','latency_ms','2026-04-15 13:57',520], // p95 spike

      // UserDB - consistent good performance (20 measurements)
      ['m21','s3','latency_ms','2026-04-15 13:00',12],
      ['m22','s3','latency_ms','2026-04-15 13:03',15],
      ['m23','s3','latency_ms','2026-04-15 13:06',13],
      ['m24','s3','latency_ms','2026-04-15 13:09',14],
      ['m25','s3','latency_ms','2026-04-15 13:12',11],
      ['m26','s3','latency_ms','2026-04-15 13:15',16],
      ['m27','s3','latency_ms','2026-04-15 13:18',13],
      ['m28','s3','latency_ms','2026-04-15 13:21',12],
      ['m29','s3','latency_ms','2026-04-15 13:24',14],
      ['m30','s3','latency_ms','2026-04-15 13:27',15],
      ['m221','s3','latency_ms','2026-04-15 13:30',12],
      ['m222','s3','latency_ms','2026-04-15 13:33',13],
      ['m223','s3','latency_ms','2026-04-15 13:36',14],
      ['m224','s3','latency_ms','2026-04-15 13:39',11],
      ['m225','s3','latency_ms','2026-04-15 13:42',15],
      ['m226','s3','latency_ms','2026-04-15 13:45',13],
      ['m227','s3','latency_ms','2026-04-15 13:48',12],
      ['m228','s3','latency_ms','2026-04-15 13:51',14],
      ['m229','s3','latency_ms','2026-04-15 13:54',16],
      ['m230','s3','latency_ms','2026-04-15 13:57',17], // p95

      // NotificationService - high latency
      ['m31','s4','latency_ms','2026-04-15 13:00',220],
      ['m32','s4','latency_ms','2026-04-15 13:05',235],
      ['m33','s4','latency_ms','2026-04-15 13:10',228],
      ['m34','s4','latency_ms','2026-04-15 13:15',242],
      ['m35','s4','latency_ms','2026-04-15 13:20',1200], // major spike
      ['m36','s4','latency_ms','2026-04-15 13:25',238],
      ['m37','s4','latency_ms','2026-04-15 13:30',245],
      ['m38','s4','latency_ms','2026-04-15 13:35',232],
      ['m39','s4','latency_ms','2026-04-15 13:40',241],
      ['m40','s4','latency_ms','2026-04-15 13:45',239],

      // Old metrics (outside 24h window)
      ['m41','s1','latency_ms','2026-04-14 12:00',55],
      ['m42','s2','latency_ms','2026-04-14 12:00',95],

      // Error rate metrics for different services
      ['m43','s1','error_rate','2026-04-15 13:00',0.1],
      ['m44','s2','error_rate','2026-04-15 13:00',0.5],
      ['m45','s5','error_rate','2026-04-15 13:00',2.3],
      ['m46','s7','error_rate','2026-04-15 13:00',1.8]
    ];

    const metricStmt = db.prepare(
      'INSERT INTO metrics (metric_id, service_id, metric_type, recorded_at, value) VALUES (?, ?, ?, ?, ?)'
    );
    metrics.forEach(m => metricStmt.run(m));
    metricStmt.free();

    // Alerts - Q2: Alert clustering and deduplication
    const alerts = [
      // s1 - Multiple related alerts (should cluster into 1 group)
      ['a1','s1','critical','2026-04-15 10:00','2026-04-15 10:15','high_latency'],
      ['a2','s1','warning','2026-04-15 10:02','2026-04-15 10:15','high_error_rate'],
      ['a3','s1','warning','2026-04-15 10:05','2026-04-15 10:15','cpu_spike'],

      // s2 - Two separate alert storms
      ['a4','s2','critical','2026-04-15 08:00','2026-04-15 08:30','database_timeout'],
      ['a5','s2','critical','2026-04-15 08:05','2026-04-15 08:30','high_latency'],
      ['a6','s2','warning','2026-04-15 11:00','2026-04-15 11:20','memory_leak'],
      ['a7','s2','warning','2026-04-15 11:03','2026-04-15 11:20','high_latency'],

      // s3 - Single isolated alert
      ['a8','s3','warning','2026-04-15 09:00','2026-04-15 09:10','disk_space'],

      // s4 - Long duration alert storm
      ['a9','s4','critical','2026-04-15 07:00','2026-04-15 07:45','queue_backup'],
      ['a10','s4','critical','2026-04-15 07:02','2026-04-15 07:45','high_latency'],
      ['a11','s4','warning','2026-04-15 07:06','2026-04-15 07:45','memory_pressure'],
      ['a12','s4','warning','2026-04-15 07:10','2026-04-15 07:45','cpu_spike'],

      // s5 - No clustering (isolated alerts > 5min apart)
      ['a13','s5','warning','2026-04-15 06:00','2026-04-15 06:05','high_error_rate'],
      ['a14','s5','warning','2026-04-15 06:20','2026-04-15 06:25','high_error_rate'],

      // s6 - Unresolved alerts (null resolved_at)
      ['a15','s6','critical','2026-04-15 13:30',null,'service_down'],
      ['a16','s6','critical','2026-04-15 13:32',null,'health_check_failed'],

      // Old alerts (outside window)
      ['a17','s1','warning','2026-04-14 10:00','2026-04-14 10:10','high_latency'],
      ['a18','s7','warning','2026-04-15 12:00','2026-04-15 12:05','disk_io']
    ];

    const alertStmt = db.prepare(
      'INSERT INTO alerts (alert_id, service_id, severity, fired_at, resolved_at, alert_type) VALUES (?, ?, ?, ?, ?, ?)'
    );
    alerts.forEach(a => alertStmt.run(a));
    alertStmt.free();

    // Incidents - Q3: SLA breach calculation
    const incidents = [
      // April incidents for SLA calculation (30 day window)
      // s1 - 2 incidents totaling 35 minutes downtime
      ['i1','s1','2026-04-01 08:00','2026-04-01 08:20','minor','database_connection'],     // 20 min
      ['i2','s1','2026-04-14 15:00','2026-04-14 15:15','minor','config_error'],             // 15 min

      // s2 - 1 major incident, 45 minutes
      ['i3','s2','2026-04-10 10:00','2026-04-10 10:45','major','upstream_dependency'],      // 45 min

      // s3 - 3 small incidents totaling 18 minutes
      ['i4','s3','2026-04-05 12:00','2026-04-05 12:05','minor','network_blip'],             // 5 min
      ['i5','s3','2026-04-12 09:00','2026-04-12 09:08','minor','slow_query'],               // 8 min
      ['i6','s3','2026-04-15 11:00','2026-04-15 11:05','minor','replica_lag'],              // 5 min

      // s4 - 1 long incident (2 hours)
      ['i7','s4','2026-04-08 14:00','2026-04-08 16:00','critical','message_queue_failure'], // 120 min

      // s5 - No incidents in April

      // s6 - Multiple small incidents
      ['i8','s6','2026-04-03 10:00','2026-04-03 10:10','minor','cache_miss'],               // 10 min
      ['i9','s6','2026-04-07 11:00','2026-04-07 11:12','minor','model_timeout'],            // 12 min
      ['i10','s6','2026-04-13 13:00','2026-04-13 13:08','minor','rate_limit'],              // 8 min

      // s10 - Ongoing incident (not resolved)
      ['i11','s10','2026-04-15 13:00',null,'critical','redis_cluster_down'],                // ongoing

      // Old incidents (outside April window)
      ['i12','s1','2026-03-15 10:00','2026-03-15 10:30','minor','deployment_issue'],
      ['i13','s2','2026-03-20 14:00','2026-03-20 14:20','minor','certificate_renewal']
    ];

    const incidentStmt = db.prepare(
      'INSERT INTO incidents (incident_id, service_id, started_at, resolved_at, impact, root_cause) VALUES (?, ?, ?, ?, ?, ?)'
    );
    incidents.forEach(i => incidentStmt.run(i));
    incidentStmt.free();
  },

  questions: [
    {
      id: 1,
      difficulty: 'Warm-up',
      timeLimit: 600, // 10 minutes
      question: `Calculate the 95th percentile (p95) latency for each tier-1 service over the last 24 hours. Only include services that have at least 5 latency measurements in this window.

Return: service_name, num_measurements, p95_latency_ms (rounded to 1 decimal). Order by p95_latency_ms DESC.

Note: "now" is pinned at 2026-04-15 14:00:00. Use NTILE(20) window function to calculate p95 - the 95th percentile is the minimum value in the top 5% bucket (tile 20).`,
      referenceSQL: `
        WITH tier1_services AS (
          SELECT service_id, service_name
          FROM services
          WHERE tier = 1
        ),
        recent_metrics AS (
          SELECT
            m.service_id,
            m.value,
            NTILE(20) OVER (PARTITION BY m.service_id ORDER BY m.value) AS percentile_bucket
          FROM metrics m
          JOIN tier1_services t ON t.service_id = m.service_id
          WHERE m.metric_type = 'latency_ms'
            AND m.recorded_at >= datetime('2026-04-15 14:00:00', '-24 hours')
        ),
        measurement_counts AS (
          SELECT service_id, COUNT(*) AS num_measurements
          FROM recent_metrics
          GROUP BY service_id
          HAVING COUNT(*) >= 5
        ),
        p95_values AS (
          SELECT
            rm.service_id,
            MIN(rm.value) AS p95_latency
          FROM recent_metrics rm
          WHERE rm.percentile_bucket = 20
          GROUP BY rm.service_id
        )
        SELECT
          t.service_name,
          mc.num_measurements,
          ROUND(p95.p95_latency, 1) AS p95_latency_ms
        FROM tier1_services t
        JOIN measurement_counts mc ON mc.service_id = t.service_id
        JOIN p95_values p95 ON p95.service_id = t.service_id
        ORDER BY p95_latency_ms DESC
      `,
      solutionSQL: `WITH tier1_services AS (
  SELECT service_id, service_name
  FROM services
  WHERE tier = 1
),
recent_metrics AS (
  SELECT
    m.service_id,
    m.value,
    NTILE(20) OVER (PARTITION BY m.service_id ORDER BY m.value) AS percentile_bucket
  FROM metrics m
  JOIN tier1_services t ON t.service_id = m.service_id
  WHERE m.metric_type = 'latency_ms'
    AND m.recorded_at >= datetime('2026-04-15 14:00:00', '-24 hours')
),
measurement_counts AS (
  SELECT service_id, COUNT(*) AS num_measurements
  FROM recent_metrics
  GROUP BY service_id
  HAVING COUNT(*) >= 5
),
p95_values AS (
  SELECT
    rm.service_id,
    MIN(rm.value) AS p95_latency
  FROM recent_metrics rm
  WHERE rm.percentile_bucket = 20
  GROUP BY rm.service_id
)
SELECT
  t.service_name,
  mc.num_measurements,
  ROUND(p95.p95_latency, 1) AS p95_latency_ms
FROM tier1_services t
JOIN measurement_counts mc ON mc.service_id = t.service_id
JOIN p95_values p95 ON p95.service_id = t.service_id
ORDER BY p95_latency_ms DESC`,
      expectedColumns: ['service_name', 'num_measurements', 'p95_latency_ms']
    },
    {
      id: 2,
      difficulty: 'Medium',
      timeLimit: 1200, // 20 minutes
      question: `Identify alert "storms" - clusters of alerts for the same service where multiple alerts fired within 5 minutes of each other. For each storm, calculate the storm duration (time from first alert fired to last alert resolved).

An alert storm is defined as: 2+ alerts for the same service where each subsequent alert fired within 5 minutes of the previous alert's fire time.

Return: service_id, storm_start (earliest fired_at in cluster), storm_end (latest resolved_at in cluster), alert_count, duration_minutes (rounded to 1 decimal). Order by duration_minutes DESC.

Exclude unresolved alerts (where resolved_at IS NULL). Only consider alerts from the last 24 hours.

Hint: Use window functions with range-based window frames to detect gaps between alerts.`,
      referenceSQL: `
        WITH resolved_alerts AS (
          SELECT
            service_id,
            alert_id,
            fired_at,
            resolved_at
          FROM alerts
          WHERE resolved_at IS NOT NULL
            AND fired_at >= datetime('2026-04-15 14:00:00', '-24 hours')
          ORDER BY service_id, fired_at
        ),
        alert_gaps AS (
          SELECT
            service_id,
            alert_id,
            fired_at,
            resolved_at,
            LAG(fired_at) OVER (PARTITION BY service_id ORDER BY fired_at) AS prev_fired_at,
            CASE
              WHEN (julianday(fired_at) - julianday(LAG(fired_at) OVER (PARTITION BY service_id ORDER BY fired_at))) * 24 * 60 > 5
                OR LAG(fired_at) OVER (PARTITION BY service_id ORDER BY fired_at) IS NULL
              THEN 1
              ELSE 0
            END AS is_new_storm
          FROM resolved_alerts
        ),
        storm_groups AS (
          SELECT
            service_id,
            alert_id,
            fired_at,
            resolved_at,
            SUM(is_new_storm) OVER (PARTITION BY service_id ORDER BY fired_at ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW) AS storm_id
          FROM alert_gaps
        ),
        storm_stats AS (
          SELECT
            service_id,
            storm_id,
            MIN(fired_at) AS storm_start,
            MAX(resolved_at) AS storm_end,
            COUNT(*) AS alert_count
          FROM storm_groups
          GROUP BY service_id, storm_id
          HAVING COUNT(*) >= 2
        )
        SELECT
          service_id,
          storm_start,
          storm_end,
          alert_count,
          ROUND((julianday(storm_end) - julianday(storm_start)) * 24 * 60, 1) AS duration_minutes
        FROM storm_stats
        ORDER BY duration_minutes DESC
      `,
      solutionSQL: `WITH resolved_alerts AS (
  SELECT
    service_id,
    alert_id,
    fired_at,
    resolved_at
  FROM alerts
  WHERE resolved_at IS NOT NULL
    AND fired_at >= datetime('2026-04-15 14:00:00', '-24 hours')
  ORDER BY service_id, fired_at
),
alert_gaps AS (
  SELECT
    service_id,
    alert_id,
    fired_at,
    resolved_at,
    LAG(fired_at) OVER (PARTITION BY service_id ORDER BY fired_at) AS prev_fired_at,
    CASE
      WHEN (julianday(fired_at) - julianday(LAG(fired_at) OVER (PARTITION BY service_id ORDER BY fired_at))) * 24 * 60 > 5
        OR LAG(fired_at) OVER (PARTITION BY service_id ORDER BY fired_at) IS NULL
      THEN 1
      ELSE 0
    END AS is_new_storm
  FROM resolved_alerts
),
storm_groups AS (
  SELECT
    service_id,
    alert_id,
    fired_at,
    resolved_at,
    SUM(is_new_storm) OVER (PARTITION BY service_id ORDER BY fired_at ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW) AS storm_id
  FROM alert_gaps
),
storm_stats AS (
  SELECT
    service_id,
    storm_id,
    MIN(fired_at) AS storm_start,
    MAX(resolved_at) AS storm_end,
    COUNT(*) AS alert_count
  FROM storm_groups
  GROUP BY service_id, storm_id
  HAVING COUNT(*) >= 2
)
SELECT
  service_id,
  storm_start,
  storm_end,
  alert_count,
  ROUND((julianday(storm_end) - julianday(storm_start)) * 24 * 60, 1) AS duration_minutes
FROM storm_stats
ORDER BY duration_minutes DESC`,
      expectedColumns: ['service_id', 'storm_start', 'storm_end', 'alert_count', 'duration_minutes']
    },
    {
      id: 3,
      difficulty: 'Hard',
      timeLimit: 1800, // 30 minutes
      question: `Calculate SLA compliance for all tier-1 and tier-2 services over the past 30 days (from 2026-03-16 00:00:00 to 2026-04-15 14:00:00). SLA is measured as uptime percentage.

For each service, calculate:
- Total possible uptime in minutes (30 days = 43,200 minutes)
- Total downtime in minutes (sum of all incident durations in the window)
- Actual uptime percentage
- SLA breach (1 if actual uptime < sla_target, 0 otherwise)

For ongoing incidents (resolved_at IS NULL), treat them as if they resolved at the calculation time (2026-04-15 14:00:00).

Return: service_name, tier, sla_target, total_downtime_minutes (rounded to 1 decimal), uptime_percentage (rounded to 3 decimals), sla_breached. Order by uptime_percentage ASC.

Important: If an incident started before the 30-day window, only count the downtime within the window. Similarly, if it resolved after, cap at the calculation time.`,
      referenceSQL: `
        WITH time_constants AS (
          SELECT
            datetime('2026-03-16 00:00:00') AS window_start,
            datetime('2026-04-15 14:00:00') AS window_end,
            (julianday('2026-04-15 14:00:00') - julianday('2026-03-16 00:00:00')) * 24 * 60 AS total_minutes
        ),
        tier_services AS (
          SELECT service_id, service_name, tier, sla_target
          FROM services
          WHERE tier IN (1, 2)
        ),
        incident_downtime AS (
          SELECT
            i.service_id,
            SUM(
              (julianday(
                CASE
                  WHEN i.resolved_at IS NULL THEN tc.window_end
                  WHEN datetime(i.resolved_at) > tc.window_end THEN tc.window_end
                  ELSE i.resolved_at
                END
              ) - julianday(
                CASE
                  WHEN datetime(i.started_at) < tc.window_start THEN tc.window_start
                  ELSE i.started_at
                END
              )) * 24 * 60
            ) AS downtime_minutes
          FROM incidents i
          CROSS JOIN time_constants tc
          WHERE datetime(i.started_at) < tc.window_end
            AND (i.resolved_at IS NULL OR datetime(i.resolved_at) > tc.window_start)
          GROUP BY i.service_id
        )
        SELECT
          ts.service_name,
          ts.tier,
          ts.sla_target,
          COALESCE(ROUND(id.downtime_minutes, 1), 0.0) AS total_downtime_minutes,
          ROUND(((tc.total_minutes - COALESCE(id.downtime_minutes, 0)) / tc.total_minutes) * 100, 3) AS uptime_percentage,
          CASE
            WHEN ((tc.total_minutes - COALESCE(id.downtime_minutes, 0)) / tc.total_minutes) * 100 < ts.sla_target
            THEN 1
            ELSE 0
          END AS sla_breached
        FROM tier_services ts
        CROSS JOIN time_constants tc
        LEFT JOIN incident_downtime id ON id.service_id = ts.service_id
        ORDER BY uptime_percentage ASC
      `,
      solutionSQL: `WITH time_constants AS (
  SELECT
    datetime('2026-03-16 00:00:00') AS window_start,
    datetime('2026-04-15 14:00:00') AS window_end,
    (julianday('2026-04-15 14:00:00') - julianday('2026-03-16 00:00:00')) * 24 * 60 AS total_minutes
),
tier_services AS (
  SELECT service_id, service_name, tier, sla_target
  FROM services
  WHERE tier IN (1, 2)
),
incident_downtime AS (
  SELECT
    i.service_id,
    SUM(
      (julianday(
        CASE
          WHEN i.resolved_at IS NULL THEN tc.window_end
          WHEN datetime(i.resolved_at) > tc.window_end THEN tc.window_end
          ELSE i.resolved_at
        END
      ) - julianday(
        CASE
          WHEN datetime(i.started_at) < tc.window_start THEN tc.window_start
          ELSE i.started_at
        END
      )) * 24 * 60
    ) AS downtime_minutes
  FROM incidents i
  CROSS JOIN time_constants tc
  WHERE datetime(i.started_at) < tc.window_end
    AND (i.resolved_at IS NULL OR datetime(i.resolved_at) > tc.window_start)
  GROUP BY i.service_id
)
SELECT
  ts.service_name,
  ts.tier,
  ts.sla_target,
  COALESCE(ROUND(id.downtime_minutes, 1), 0.0) AS total_downtime_minutes,
  ROUND(((tc.total_minutes - COALESCE(id.downtime_minutes, 0)) / tc.total_minutes) * 100, 3) AS uptime_percentage,
  CASE
    WHEN ((tc.total_minutes - COALESCE(id.downtime_minutes, 0)) / tc.total_minutes) * 100 < ts.sla_target
    THEN 1
    ELSE 0
  END AS sla_breached
FROM tier_services ts
CROSS JOIN time_constants tc
LEFT JOIN incident_downtime id ON id.service_id = ts.service_id
ORDER BY uptime_percentage ASC`,
      expectedColumns: ['service_name', 'tier', 'sla_target', 'total_downtime_minutes', 'uptime_percentage', 'sla_breached']
    }
  ]
};

export default infraTest;
